import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import {
  DELETE_RIGHT_NOW_PHRASE,
  type ScheduledDeletionInterface,
} from "shared-core/types/account-deletion";
import { formatDate } from "shared-core/utils/format";
import { computed, inject, type Ref } from "vue";
import { useI18n } from "vue-i18n";
import {
  type AuthStore,
  type AuthStoreActions,
  AuthStoreInjectKey,
} from "../types/auth-store";
import { resolveApiErrorDetail } from "../utils/admin-helpers";
import { useApi } from "../utils/api";
import { DeletionTarget } from "./use-account-deletion";
import { useTypedPhrase } from "./use-typed-phrase";

export function useDeleteRightNow(
  target: Readonly<Ref<DeletionTarget | null>>,
  busy: Ref<boolean>,
): void {
  const api = useApi();
  const confirm = useConfirm();
  const toast = useToast();
  const i18n = useI18n();
  const { t, locale } = i18n;
  const te = i18n.te.bind(i18n);
  const authStore = inject<AuthStore & AuthStoreActions>(AuthStoreInjectKey)!;

  function scheduledFor(current: DeletionTarget): string | null | undefined {
    return current === DeletionTarget.ACCOUNT
      ? authStore.user?.deleteAt
      : authStore.user?.organizationDeleteAt;
  }

  // The refresh can fail without a word, so the gate shows the deadline the server answered with.
  function applyDeadline(current: DeletionTarget, deleteAt: string): void {
    if (!authStore.user) return;
    authStore.user =
      current === DeletionTarget.ACCOUNT
        ? { ...authStore.user, deleteAt }
        : { ...authStore.user, organizationDeleteAt: deleteAt };
  }

  useTypedPhrase(
    DELETE_RIGHT_NOW_PHRASE,
    computed(() => target.value !== null),
    () => {
      const current = target.value;
      const deleteAt = current && scheduledFor(current);
      if (!current || !deleteAt || busy.value) return;
      confirm.require({
        header: t("shared_ui.scheduled_deletion_gate.delete_now_header"),
        message: t(`shared_ui.scheduled_deletion_gate.delete_now_${current}`, {
          date: formatDate(deleteAt, locale.value, "long"),
        }),
        acceptLabel: t("shared_ui.scheduled_deletion_gate.delete_now_confirm"),
        rejectLabel: t("shared_ui.scheduled_deletion_gate.delete_now_cancel"),
        acceptClass: "p-button-danger",
        defaultFocus: "reject",
        accept: () => void deleteRightNow(current),
      });
    },
  );

  // A deadline that is due ends the session, so the refresh gets a 401 and each app signs out its own way.
  async function refreshUser(): Promise<void> {
    await authStore.checkAuth(true).catch((error: unknown) => {
      console.error(
        "Failed to refresh the user after deleting right now",
        error,
      );
    });
  }

  async function deleteRightNow(current: DeletionTarget): Promise<void> {
    busy.value = true;
    try {
      const response = await api.post<ScheduledDeletionInterface>(
        `${current}/deletion/now`,
      );
      applyDeadline(current, (await response.data).deleteAt);
      toast.add({
        severity: "success",
        summary: t("shared_ui.scheduled_deletion_gate.delete_now_done"),
        life: 5000,
      });
    } catch (error) {
      console.error("Failed to delete right now", error);
      toast.add({
        severity: "error",
        summary: t("common.error"),
        detail: resolveApiErrorDetail(
          error,
          t,
          te,
          "shared_ui.scheduled_deletion_gate.delete_now_failed",
        ),
        life: 5000,
      });
    }
    // A request that failed on the way back may still have moved the deadline.
    await refreshUser();
    busy.value = false;
  }
}
